"use client";

import { useState } from "react";
import { usePortfolioBuilderStore } from "@/stores/portfolioBuilderStore";
import { useOrganization } from "@/hooks/useOrganization";
import { StrategyRecommendationCard } from "./StrategyRecommendationCard";
import { PortfolioMetricsPanel } from "./PortfolioMetricsPanel";
import { ScoreBreakdownChart } from "./ScoreBreakdownChart";
import { EquityCurveChart } from "./EquityCurveChart";
import { ApplyConfirmationModal } from "./ApplyConfirmationModal";

function formatCurrency(n: number): string {
  return "$" + n.toLocaleString("en-US");
}

export function RecommendationStep() {
  const [showApplyModal, setShowApplyModal] = useState(false);
  const { recommendation, insufficientCatalog, reset, setStep } =
    usePortfolioBuilderStore();
  const { isViewOnly } = useOrganization();

  // SFWL-011: tenant's enabled+scoped catalog has fewer strategies than the
  // engine requires. Distinct empty state — no Apply button, since there is
  // no recommendation to apply.
  if (insufficientCatalog) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">
          Your administrator hasn&apos;t enabled strategies yet.
        </p>
        <button
          onClick={() => {
            reset();
            setStep(1);
          }}
          className="mt-4 text-sm text-blue-400 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  if (!recommendation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No recommendation available.</p>
        <button
          onClick={() => setStep(3)}
          className="mt-4 text-sm text-blue-400 hover:underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const { name, strategies, metrics, scores, equityCurve, derivedProfile } =
    recommendation;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{name}</h2>
          <p className="text-sm text-gray-400">
            {strategies.length} strateg{strategies.length !== 1 ? "ies" : "y"} ·{" "}
            {formatCurrency(metrics.totalEquity)} total
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-blue-400">
            {scores.totalScore.toFixed(1)}
          </div>
          <div className="text-xs text-gray-500">Match Score</div>
        </div>
      </div>

      {/* Profile summary */}
      <div className="grid grid-cols-4 gap-3 rounded-lg border border-gray-800 bg-gray-900/50 p-3 text-center text-xs">
        <div>
          <div className="text-gray-500">Experience</div>
          <div className="mt-0.5 font-medium">
            {derivedProfile.overallExperience.toFixed(1)} / 5
          </div>
        </div>
        <div>
          <div className="text-gray-500">Leverage</div>
          <div className="mt-0.5 font-medium">{derivedProfile.leverageFactor}x</div>
        </div>
        <div>
          <div className="text-gray-500">Budget</div>
          <div className="mt-0.5 font-medium text-blue-400">
            {formatCurrency(derivedProfile.totalAllocation)}
          </div>
        </div>
        <div>
          <div className="text-gray-500">Allocation %</div>
          <div className="mt-0.5 font-medium">
            {derivedProfile.allocationPercentage.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Strategy cards */}
      <div>
        <h3 className="mb-2 text-sm font-semibold">Recommended Strategies</h3>
        <div className="space-y-2">
          {strategies.map((s) => (
            <StrategyRecommendationCard key={s.strategyId} strategy={s} />
          ))}
        </div>
      </div>

      {/* Metrics */}
      <PortfolioMetricsPanel metrics={metrics} />

      {/* Score breakdown & equity curve side-by-side on larger screens */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-4">
          <ScoreBreakdownChart scores={scores} />
        </div>
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-4">
          <EquityCurveChart data={equityCurve} totalEquity={metrics.totalEquity} />
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between">
        <button
          onClick={() => {
            reset();
            setStep(1);
          }}
          className="rounded-lg border border-gray-700 px-6 py-2.5 text-sm text-gray-300 hover:bg-gray-800 transition-colors"
        >
          Start Over
        </button>
        {!isViewOnly && (
          <button
            onClick={() => setShowApplyModal(true)}
            className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium hover:bg-blue-500 transition-colors"
          >
            Apply Portfolio
          </button>
        )}
      </div>

      <ApplyConfirmationModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        strategies={strategies}
        totalEquity={metrics.totalEquity}
      />
    </div>
  );
}
